import { fecha, useTraduccion } from '@tallerya/i18n';
import { AvisoError, Ayuda, Boton, Chip, Menu, type ElementoMenu } from '@tallerya/ui';
import { useState, type ReactNode } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router';
import { useTituloPantalla } from '../../layout/titulo';
import { useAccionDeFila } from '../../componentes/useAccionDeFila';
import { CambiarCodigoPersona } from './CambiarCodigoPersona';
import { EliminarPersona } from './EliminarPersona';
import {
  nombreDe,
  useCambiosPersona,
  useFichaPersona,
  useSucursalesDelTaller,
  type FichaPersona as Ficha,
} from './datos';

// La ficha de una persona del taller (#58): «Ficha del mecánico» del Figma, con
// lo que hoy existe. Sigue a `FichaProveedor.tsx`: migas, cabecera con sus
// acciones y secciones de solo lectura.
//
// Lo que el Figma dibuja y no está aquí:
//
// - «Acceso al sistema» y «Dispositivos»: son de #208 (fase 4).
// - «Trabajo reciente»: las líneas que marcó cumplidas. Necesita su consulta, y
//   va en un issue aparte.
//
// Se llega desde el listado, que manda `desdeListado` en el estado: «Atrás»
// vuelve a él con sus filtros. Quien entró por un enlace va al listado limpio.

const RUTA = '/personal';

export function FichaPersona() {
  const { t } = useTraduccion();
  const navegar = useNavigate();
  const { id } = useParams();
  const consulta = useFichaPersona(id);
  useTituloPantalla(consulta.data ? nombreDe(consulta.data) : t('admin.personal.titulo'));

  // Al eliminar desde aquí no se llega a ver «no existe»: `eliminar` no vuelve a
  // pedir la ficha eliminada, y se sigue viendo hasta que se sale.
  const ficha = consulta.data;

  if (consulta.isPending) return <p className="entrada">{t('app.cargando')}</p>;

  if (!ficha) {
    const noExiste = consulta.data === null;
    return (
      <section className="aviso">
        <h2>{noExiste ? t('admin.personal.noExiste.titulo') : t('ui.listado.error.titulo')}</h2>
        <p>{noExiste ? t('admin.personal.noExiste.texto') : t('ui.listado.error.texto')}</p>
        <Boton variante="secundario" onClick={() => void navegar(RUTA)}>
          {t('admin.personal.volver')}
        </Boton>
      </section>
    );
  }

  return <Contenido ficha={ficha} />;
}

function Contenido({ ficha }: { ficha: Ficha }) {
  const { t, idioma } = useTraduccion();
  const navegar = useNavigate();
  const cambios = useCambiosPersona();
  const sucursales = useSucursalesDelTaller();
  const { hacer, problema } = useAccionDeFila();
  const [eliminando, setEliminando] = useState(false);
  const [cambiandoCodigo, setCambiandoCodigo] = useState(false);
  const id = String(ficha.id);
  const nombre = nombreDe(ficha);

  const desdeListado = (useLocation().state as { desdeListado?: boolean } | null)?.desdeListado ?? false;
  // Eliminada, la ficha ya no existe: se vuelve al listado que hay detrás, con
  // sus filtros, o se reemplaza esta pantalla por uno limpio.
  const alEliminar = () => void (desdeListado ? navegar(-1) : navegar(RUTA, { replace: true }));

  const masAcciones: ElementoMenu[] = [
    { etiqueta: t('admin.personal.menu.cambiarCodigo'), onSeleccionar: () => setCambiandoCodigo(true) },
    ficha.isActive
      ? {
          etiqueta: t('admin.personal.menu.desactivar'),
          onSeleccionar: () =>
            void hacer(cambios.cambiarEstado(id, false), t('admin.personal.aviso.desactivado', { nombre })),
        }
      : {
          etiqueta: t('admin.personal.menu.activar'),
          onSeleccionar: () =>
            void hacer(cambios.cambiarEstado(id, true), t('admin.personal.aviso.activado', { nombre })),
        },
    { tipo: 'divisor' },
    { etiqueta: t('admin.personal.menu.eliminar'), peligro: true, onSeleccionar: () => setEliminando(true) },
  ];

  const nombreSucursal = new Map((sucursales.data ?? []).map((s) => [String(s.id), s.name]));
  // «Todas» solo si no tiene ninguna. Con sucursales cuyos nombres no llegaron
  // —la lista pide `settings.edit`, o aún carga— va un guion, no «Todas».
  const enSucursales =
    ficha.locations.length === 0
      ? t('admin.personal.ficha.sinSucursales')
      : ficha.locations
          .map((l) => nombreSucursal.get(String(l)))
          .filter(Boolean)
          .join(' · ') || '—';

  return (
    <>
      <nav aria-label={t('admin.clientes.ficha.migas')}>
        <ol className="ficha__migas">
          <li>
            <Link to={RUTA}>{t('admin.personal.titulo')}</Link>
          </li>
          <li aria-current="page">{nombre}</li>
        </ol>
      </nav>

      {problema && <AvisoError aviso={{ titulo: t('admin.personal.error.titulo'), texto: problema }} />}

      <header className="tipos__cabecera">
        <div>
          <div className="tipos__titulo">
            <h1>{nombre}</h1>
            <Ayuda tema={nombre} texto={t('admin.personal.ficha.info')} tono="titulo" />
            {ficha.isActive ? (
              <Chip tono="exito">{t('admin.personal.activo')}</Chip>
            ) : (
              <Chip>{t('admin.personal.inactivo')}</Chip>
            )}
          </div>
          <p className="tipos__resumen">
            {[ficha.employeeCode, t('admin.personal.ficha.desde', { fecha: fecha(ficha.createdAt, idioma) })].join(' · ')}
          </p>
        </div>
        <div className="tipos__acciones">
          <Boton
            variante="secundario"
            // `desdeFicha`: el formulario vuelve con «Atrás», que es esta ficha. Y
            // `desdeListado`, si hay un listado detrás de ella: al eliminar desde
            // la edición se salta hasta ahí.
            onClick={() => void navegar(`${RUTA}/${id}/editar`, { state: { desdeFicha: true, desdeListado } })}
          >
            {t('admin.personal.menu.editar')}
          </Boton>
          <Menu
            elementos={masAcciones}
            disparador={({ alternar, atributos }) => (
              <Boton variante="secundario" soloIcono aria-label={t('admin.personal.mas')} onClick={alternar} {...atributos}>
                •••
              </Boton>
            )}
          />
        </div>
      </header>

      <div className="ficha__secciones">
        <section className="tipos__seccion">
          <div className="tipos__seccion-cabecera">
            <div className="titulo-con-ayuda titulo-con-ayuda--seccion">
              <h2>{t('admin.personal.ficha.datos')}</h2>
              <Ayuda
                tema={t('admin.personal.ficha.datos')}
                texto={t('admin.personal.ficha.datosInfo')}
                tono="titulo"
              />
            </div>
          </div>
          <dl className="ficha__datos">
            <Dato etiqueta={t('admin.personal.formulario.telefono')}>{ficha.phone ?? '—'}</Dato>
            <Dato etiqueta={t('admin.personal.formulario.correo')}>
              {ficha.email ?? t('admin.personal.ficha.sinCorreo')}
            </Dato>
            <Dato etiqueta={t('admin.personal.formulario.ingreso')}>
              {/* Una fecha sin hora: se lee como día del calendario, sin pasar por zonas horarias. */}
              {ficha.hiredOn ? fecha(`${ficha.hiredOn}T12:00:00Z`, idioma) : '—'}
            </Dato>
            <Dato etiqueta={t('admin.personal.formulario.sucursales')}>
              {enSucursales}
            </Dato>
            <Dato etiqueta={t('admin.personal.formulario.especialidades')}>
              {ficha.specialties.length > 0 ? (
                <span className="ficha__chips">
                  {ficha.specialties.map((e) => (
                    <Chip key={e}>{e}</Chip>
                  ))}
                </span>
              ) : (
                t('admin.personal.ficha.sinEspecialidades')
              )}
            </Dato>
          </dl>
        </section>
      </div>

      <EliminarPersona persona={eliminando ? ficha : null} onCerrar={() => setEliminando(false)} onEliminado={alEliminar} />
      <CambiarCodigoPersona persona={cambiandoCodigo ? ficha : null} onCerrar={() => setCambiandoCodigo(false)} />
    </>
  );
}

function Dato({ etiqueta, children }: { etiqueta: string; children: ReactNode }) {
  return (
    <div className="ficha__dato">
      <dt>{etiqueta}</dt>
      <dd>{children}</dd>
    </div>
  );
}
